/**
 * Ground OVERRIDE helpers: field sources and autofill guards.
 * Manual fields must not be clobbered by soil/zone lookup.
 *
 * @see yona-project/planting-assistant/ground-data-contract.md
 */

export const GROUND_FIELD_SOURCE = Object.freeze({
  AUTO: 'auto',
  MANUAL: 'manual',
});

/** Keys the Ground OVERRIDE panel can edit (v1 / G1–G2). */
export const GROUND_OVERRIDE_FIELDS = Object.freeze([
  'zone',
  'soilType',
  'soilDrainage',
  'soilPh',
  'frost',
]);

export function normalizeGroundFieldSource(value) {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === GROUND_FIELD_SOURCE.MANUAL) return GROUND_FIELD_SOURCE.MANUAL;
  if (raw === GROUND_FIELD_SOURCE.AUTO) return GROUND_FIELD_SOURCE.AUTO;
  return '';
}

/** True when a subsequent survey/zone lookup must leave this field alone. */
export function isManualGroundSource(source) {
  return normalizeGroundFieldSource(source) === GROUND_FIELD_SOURCE.MANUAL;
}

export function shouldApplyAutofill(source) {
  return !isManualGroundSource(source);
}

/**
 * Pick which autofill keys may write into draft state.
 * @param {Record<string, string>} sources - zone | soilType | soilDrainage | soilPh | frost
 * @param {string[]} keys
 * @returns {string[]}
 */
export function autofillKeysAllowed(sources = {}, keys = GROUND_OVERRIDE_FIELDS) {
  return keys.filter((key) => shouldApplyAutofill(sources?.[key]));
}

/**
 * Build a patch of incoming survey/zone values filtered by manual sources.
 * Only includes keys present on `incoming` that are allowed to autofill.
 */
export function filterAutofillPatch(sources = {}, incoming = {}) {
  const patch = {};
  Object.keys(incoming || {}).forEach((key) => {
    if (!shouldApplyAutofill(sources?.[key])) return;
    if (incoming[key] === undefined) return;
    patch[key] = incoming[key];
  });
  return patch;
}

/**
 * Frost tile copy from last / first labels (e.g. "MAR 21 – NOV 12").
 */
export function formatFrostWindowLabel(lastLabel = '', firstLabel = '') {
  const last = String(lastLabel || '').trim();
  const first = String(firstLabel || '').trim();
  if (last && first) return `${last} – ${first}`;
  if (last) return last;
  if (first) return first;
  return '';
}

/**
 * Normalize lookup-zone frost payload into draft frost labels.
 */
export function frostLabelsFromZoneResult(frost) {
  if (!frost || typeof frost !== 'object') {
    return { lastLabel: '', firstLabel: '' };
  }
  return {
    lastLabel: String(frost.last_frost_label || '').trim(),
    firstLabel: String(frost.first_frost_label || '').trim(),
  };
}

const FROST_MONTHS = Object.freeze([
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
]);

const MONTH_NAME_TO_NUM = Object.freeze(
  FROST_MONTHS.reduce((acc, month) => {
    acc[month.label.toLowerCase()] = month.value;
    acc[month.label.slice(0, 3).toLowerCase()] = month.value;
    return acc;
  }, {})
);

export function frostMonthOptions() {
  return FROST_MONTHS;
}

export function daysInFrostMonth(month) {
  const n = Number(month);
  if (n === 2) return 29;
  if ([4, 6, 9, 11].includes(n)) return 30;
  if (n >= 1 && n <= 12) return 31;
  return 31;
}

/**
 * Parse "March 20", "Mar 20", or "3/20" into { month, day }.
 */
export function parseFrostMonthDay(label) {
  const raw = String(label || '').trim();
  if (!raw) return { month: 0, day: 0 };
  const iso = /^(\d{1,2})[/-](\d{1,2})$/.exec(raw);
  if (iso) {
    const month = Number(iso[1]);
    const day = Number(iso[2]);
    if (month >= 1 && month <= 12 && day >= 1 && day <= daysInFrostMonth(month)) {
      return { month, day };
    }
  }
  const parts = raw.split(/\s+/);
  if (parts.length >= 2) {
    const month = MONTH_NAME_TO_NUM[parts[0].toLowerCase()] || 0;
    const day = parseInt(parts[1], 10);
    if (month && Number.isFinite(day) && day >= 1 && day <= daysInFrostMonth(month)) {
      return { month, day };
    }
  }
  return { month: 0, day: 0 };
}

export function formatFrostMonthDay(month, day) {
  const monthNum = Number(month);
  const dayNum = Number(day);
  const entry = FROST_MONTHS.find((item) => item.value === monthNum);
  if (!entry || !Number.isFinite(dayNum) || dayNum < 1) return '';
  const clamped = Math.min(dayNum, daysInFrostMonth(monthNum));
  return `${entry.label} ${clamped}`;
}

export function frostLabelsForZone(frostWindows, zoneCode) {
  const code = String(zoneCode || '').trim().toLowerCase();
  const entry = frostWindows && code ? frostWindows[code] : null;
  if (!entry || typeof entry !== 'object') {
    return { lastLabel: '', firstLabel: '' };
  }
  return {
    lastLabel: String(entry.last_frost || '').trim(),
    firstLabel: String(entry.first_frost || '').trim(),
  };
}

/** Empty source map for Reset to survey. */
export function emptyGroundFieldSources() {
  return {
    zone: '',
    soilType: '',
    soilDrainage: '',
    soilPh: '',
    frost: '',
  };
}
